import { AssetAssignment, AssetUsageType } from '../types/rest/mockServiceTypes';
import _ from 'lodash';
import { AssetAssignmentBundle, SslConfig, SSlConfigMode } from 'views/MockServices/Tabs/MockServices/SslConfiguration';

/**
 * Gets currently used asset from asset bundle based on the SSLConfig.
 * @param assetsBundle
 * @param key
 * @param configMode
 */
export const getCurrentlyUsedAssetByConfigMode = (
  assetsBundle: AssetAssignmentBundle,
  key: 'assets' | 'configKeyAssets',
  configMode: SSlConfigMode,
): AssetAssignment | undefined => {
  if (_.isNil(assetsBundle)) {
    return undefined;
  }
  return getAssetByCurrentMode(assetsBundle[key], configMode);
};

/**
 * Gets asset from list by current ssl config mode.
 * @param assetAssignment list of assets
 * @param configMode SSL Config mode
 */
export const getAssetByCurrentMode = (assetAssignment: AssetAssignment[], configMode: SSlConfigMode) => {
  return _.find(assetAssignment, cert => {
    if (_.isNil(cert.assetUsageType)) {
      return undefined;
    }
    // There can be always only one type selected for either client / server mode.
    const usageTypeStartsWith = configMode === SSlConfigMode.SERVER ? 'SERVER_' : 'CLIENT_';
    return cert.assetUsageType.startsWith(usageTypeStartsWith);
  });
};

/**
 * Resolves asset usage type by the context and certificate file.
 * @param type - SslConfig type
 * @param configMode - currently used config mode
 * TODO - unit test this
 */
export const resolveAssetUsageType = (type: SslConfig, configMode: SSlConfigMode): AssetUsageType => {
  let usageType: AssetUsageType;
  if (configMode === SSlConfigMode.SERVER) {
    switch (Number(type)) {
      case SslConfig.ONE_WAY:
        usageType = 'SERVER_KEYSTORE';
        break;
      case SslConfig.TWO_WAY:
        usageType = 'SERVER_KEYSTORE_TRUSTSTORE';
        break;
    }
  } else if (configMode === SSlConfigMode.CLIENT) {
    switch (Number(type)) {
      case SslConfig.ONE_WAY:
        if (isTrustStoreCert(SslConfig.ONE_WAY, configMode)) {
          usageType = 'CLIENT_TRUSTSTORE_CERT';
        }
        break;
      case SslConfig.TWO_WAY:
        usageType = 'CLIENT_KEYSTORE_TRUSTSTORE';
        break;
    }
  }

  if (_.isNil(usageType)) {
    //If this happens, check previous git revision, we might have missing case
    throw Error('Usage type not supported');
  }

  return usageType;
};

/**
 * If we are in Client mode and One Way SSL option is used, we allow only to select
 * a certificate (not a keystore).
 * @param type
 * @param configMode
 */
export const isTrustStoreCert = (type: SslConfig, configMode: SSlConfigMode) => {
  return type === SslConfig.ONE_WAY && configMode === SSlConfigMode.CLIENT;
};

/**
 * Based on current type of the SSL Config  and config mode get the selected id and alias from the asset.
 * If we are using configurable asset, we use 0 as certId.
 * @param assetsBundle wrapper holding our asset usage arrays
 * @param type type of the config used (NoSSL, ONE_WAY or TWO_WAY)
 * @param configMode type of the ssl mode currently used (SERVER / CLIENT)
 */
export const getSelectedKeysFromAssetBundle = (
  assetsBundle: AssetAssignmentBundle,
  type: SslConfig,
  configMode: SSlConfigMode,
): {
  certificateId: number;
  selectedAlias?: string;
  configKeyValue?: string;
} => {
  const usageType = resolveAssetUsageType(type, configMode);
  const configAssets = _.get(assetsBundle, 'configKeyAssets');
  const configProperty = _.find(configAssets, item => item.assetUsageType === usageType);
  if (!_.isNil(configProperty)) {
    return {
      certificateId: 0,
      configKeyValue: configProperty ? configProperty.configurationKey : '',
    };
  }

  const { assetUsageType, assetId, alias } =
    getCurrentlyUsedAssetByConfigMode(assetsBundle, 'assets', configMode) || {};
  let selectedCertId: number;
  let selectedAlias: string;

  //Server mode selection
  if (configMode === SSlConfigMode.SERVER) {
    const isTwoWaySelected = type === SslConfig.TWO_WAY && assetUsageType === 'SERVER_KEYSTORE_TRUSTSTORE';
    const isOneWaySelected = assetUsageType === 'SERVER_KEYSTORE';
    if (isTwoWaySelected || isOneWaySelected) {
      selectedCertId = assetId;
      selectedAlias = alias;
    }
  }

  //Client mode selection
  if (configMode === SSlConfigMode.CLIENT) {
    const isTwoWaySelected = type === SslConfig.TWO_WAY && assetUsageType === 'CLIENT_KEYSTORE_TRUSTSTORE';
    const isOneWaySelected = type === SslConfig.ONE_WAY && assetUsageType === 'CLIENT_TRUSTSTORE_CERT';
    if (isTwoWaySelected || isOneWaySelected) {
      selectedCertId = assetId;
      selectedAlias = alias;
    }
  }

  return {
    certificateId: selectedCertId,
    selectedAlias: selectedAlias,
  };
};

/**
 * When user selects certificate, we need to add it to asset bundle and remove all existing
 * assets from the bundle (configKeyAssets and assets)
 *
 * @param assetBundle to be updated
 * @param certificate to be added
 */
export const addCertificateToAssetBundle = (assetBundle: AssetAssignmentBundle, certificate: CertAssignment) => {
  //If bundle does not exist, create new
  if (_.isNil(assetBundle)) {
    return { assets: addCertificateToAssets([], certificate), configKeyAssets: [] };
  }

  //Add cert to the assets (and remove existing)
  let assets = _.isNil(assetBundle.assets) ? [] : [...assetBundle.assets];
  assets = addCertificateToAssets(assets, certificate);

  //Remove from config keys
  let existingConfigKeyAssets = _.isNil(assetBundle.configKeyAssets) ? [] : [...assetBundle.configKeyAssets];
  existingConfigKeyAssets = deleteAssetByUsageType(existingConfigKeyAssets, certificate.usageType);

  return { ...assetBundle, assets: assets, configKeyAssets: existingConfigKeyAssets };
};

export type CertAssignment = { certId: number; certificateName: string; usageType: AssetUsageType };

export const deleteAssetBySslMode = (assetAssignments: AssetAssignment[], sslMode: SSlConfigMode) => {
  return _.filter(assetAssignments, asset => {
    if (sslMode === SSlConfigMode.SERVER) {
      return !asset.assetUsageType.startsWith('SERVER_');
    } else {
      return !asset.assetUsageType.startsWith('CLIENT_');
    }
  });
};

/**
 * Removes item from the list of asset assignments based on the usage type prefix.
 */
export const deleteAssetByUsageType = (assetAssignments: AssetAssignment[], usageType: AssetUsageType) => {
  if (_.isNil(usageType)) {
    return assetAssignments;
  }

  const prefix = usageType.split('_')[0];
  return assetAssignments.filter(asset => {
    if (_.isNil(asset.assetUsageType)) {
      return false;
    }
    return !asset.assetUsageType.startsWith(prefix);
  });
};

/**
 * Adds certificate to existing list of assets. Removes any existing associations
 * (there can be only single server_* and client_* association)
 * @param assetAssignments - existing asset assignments
 * @param certificate - certificate to be added
 */
const addCertificateToAssets = (assetAssignments: AssetAssignment[], certificate: CertAssignment) => {
  assetAssignments = deleteAssetByUsageType(assetAssignments, certificate.usageType);
  assetAssignments.push({ assetId: certificate.certId, assetUsageType: certificate.usageType });
  return assetAssignments;
};

/**
 * Handles changes of the asset bundle. If there is an update of the property, we need to build the asset
 * bundle with config asset object and make sure the same type does not exist in assets.
 * @param assetBundle
 * @param propertyValue
 * @param usageType
 */
export const updateConfigurablePropertyInBundle = (
  assetBundle: AssetAssignmentBundle,
  propertyValue: string,
  usageType: AssetUsageType,
): AssetAssignmentBundle => {
  const configAsset = { assetUsageType: usageType, configurationKey: propertyValue };

  //If asset bundle does not exist yet
  if (_.isNil(assetBundle)) {
    return {
      assets: [],
      configKeyAssets: [configAsset],
    };
  }

  //We need to remove the type from assets array
  let assets = [];
  if (!_.isNil(assetBundle.assets) || _.size(assetBundle.assets) !== 0) {
    assets = deleteAssetByUsageType(assetBundle.assets, usageType);
  }

  // We also need to delete current usage type from config array
  let configAssets = [];
  if (!_.isNil(assetBundle.configKeyAssets) || _.size(assetBundle.configKeyAssets) !== 0) {
    configAssets = deleteAssetByUsageType(assetBundle.configKeyAssets, usageType);
  }

  configAssets.push(configAsset);

  return {
    assets: assets,
    configKeyAssets: configAssets,
  };
};

/**
 * Removes all assets related to certain ssl mode from the bundle.
 * @param assetBundle
 * @param mode
 */
export const removeAssetsByConfigMode = (assetBundle: AssetAssignmentBundle, mode: SSlConfigMode) => {
  const update: AssetAssignmentBundle = { ...assetBundle };
  update.assets = deleteAssetBySslMode(update.assets, mode);
  update.configKeyAssets = deleteAssetBySslMode(update.configKeyAssets, mode);
  return update;
};
